<template>

    <!-- === PROJECT === -->
    <!-- If session_id or experiment_id provided: hide project entirely -->
    <!-- If project_id provided: show project info row -->
    <!-- Otherwise: show project selector -->
    <div v-if="hasSessionIdInDefaults || hasExperimentIdInDefaults" />
    <div v-else-if="hasProjectIdInDefaults" class="col-12 form__control-group field-group">
      <label class="label-spacing">Project</label>
      <UidName :id="projectIdFromDefaults" :name="projectNameFromDefaults" prefix="p" />
    </div>
    <div v-else class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-project-input-${parent}-${field.name}`">
        Project
        <SVGIcon
          v-if="field.description"
          v-tooltip="field.description"
          :iconName="'info-square'"
          class="info-icon"
        />
      </label>
      <select
        :id="`select-project-input-${parent}-${field.name}`"
        :value="selectedProjectId"
        class="select"
        @input="onChangeProject(parseInt($event.target.value))"
      >
        <option value="" disabled selected>Select Project</option>
        <option
          v-for="(project, i) in projects"
          :key="`dropdown-project-${parent}-${field.name}-${i}-${project.id}`"
          :value="project.id"
        >
          {{ project.name }}
        </option>
      </select>
    </div>

    <!-- === EXPERIMENT === -->
    <!-- If session_id provided: hide experiment entirely -->
    <!-- If experiment_id provided: show experiment info row -->
    <!-- Otherwise: show experiment selector (when project is selected) -->
    <div v-if="hasSessionIdInDefaults" />
    <div v-else-if="hasExperimentIdInDefaults" class="col-12 form__control-group field-group">
      <label class="label-spacing">Experiment</label>
      <UidName :id="experimentIdFromDefaults" :name="experimentNameFromDefaults" prefix="exp" />
    </div>
    <div v-else-if="selectedProjectId || hasProjectIdInDefaults" class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-experiment-input-${parent}-${field.name}`">
        Experiment
        <SVGIcon
          v-if="field.description"
          v-tooltip="'Select an experiment'"
          :iconName="'info-square'"
          class="info-icon"
        />
      </label>
      <select
        :id="`select-experiment-input-${parent}-${field.name}`"
        :value="selectedExperimentId"
        class="select"
        @input="onChangeExperiment(parseInt($event.target.value))"
      >
        <option value="" disabled selected>Select Experiment</option>
        <option
          v-for="(experiment, i) in experiments"
          :key="`dropdown-experiment-${parent}-${field.name}-${i}-${experiment.id}`"
          :value="experiment.id"
        >
          {{ experiment.name }}
        </option>
      </select>
    </div>

    <!-- === TRAINING SESSION === -->
    <!-- If session_id provided: show session info row -->
    <!-- Otherwise: show session selector -->
    <div v-if="hasSessionIdInDefaults" class="col-12 form__control-group field-group">
      <label class="label-spacing">Training Session</label>
      <UidName :id="sessionIdFromDefaults" :name="sessionNameFromDefaults" prefix="t" />
    </div>
    <div v-else class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-training-session-input-${parent}-${field.name}`">
        Training Session
        <SVGIcon
          v-if="field.description"
          v-tooltip="'Select a training session'"
          :iconName="'info-square'"
          class="info-icon"
        />
      </label>
      <select
        :id="`select-training-session-input-${parent}-${field.name}`"
        :value="selectedTrainingSessionId"
        class="select"
        @input="onChangeTrainingSession(parseInt($event.target.value))"
      >
        <option value="" disabled selected>Select Training Session</option>
        <option
          v-for="(session, i) in trainingSessions"
          :key="`dropdown-training-session-${parent}-${field.name}-${i}-${session.id}`"
          :value="session.id"
        >
          {{ session.name || `Session ${session.id}` }}
        </option>
      </select>
    </div>

    <div v-if="enableArtifacts && selectedTrainingSessionId" class="col-12 form__control-group field-group">
      <label class="label-spacing" :for="`select-artifact-input-${parent}-${field.name}`">
        Artifacts
        <SVGIcon
          v-if="field.description"
          v-tooltip="'Select an artifact'"
          :iconName="'info-square'"
          class="info-icon"
        />
      </label>
      <select
        :id="`select-artifact-input-${parent}-${field.name}`"
        :value="selectedArtifact"
        class="select"
        @input="onChangeArtifact($event.target.value)"
      >
        <option value="" disabled selected>Select Artifact</option>
        <option
          v-for="(artifact, i) in artifacts"
          :key="`dropdown-artifact-${parent}-${field.name}-${i}-${artifact.name || artifact}`"
          :value="artifact.name || artifact"
        >
          {{ artifact.name || artifact }}
        </option>
      </select>
    </div>

</template>

<script setup>
import {
  ref, toRefs, onMounted, watch, computed,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import SVGIcon from '@/components/SVGIcon.vue';
import UidName from '@/components/Schema/UidName.vue';
import { filterSessionsByModel } from '@/utils/weightsSource';

const props = defineProps({
  parent: {
    type: String,
    default: '',
  },
  modelValue: {
    type: Object,
    default: () => ({}),
  },
  field: {
    type: Object,
    default: () => {},
  },
  projectID: {
    type: [Number, String],
    default: null,
  },
  experimentID: {
    type: [Number, String],
    default: null,
  },
  defaultValues: {
    type: Object,
    default: () => ({}),
  },
});

const {
  parent,
  field,
} = toRefs(props);

const emit = defineEmits(['update:modelValue']);

const projects = ref([]);
const selectedProjectId = ref(null);
const experiments = ref([]);
const selectedExperimentId = ref(null);
const trainingSessions = ref([]);
const selectedTrainingSessionId = ref(null);
const artifacts = ref([]);
const selectedArtifact = ref('');

const hasValidId = (value) => value !== undefined && value !== null && value !== '';

// Computed properties for default values
const hasProjectIdInDefaults = computed(() => hasValidId(props.defaultValues?.project_id));
const hasExperimentIdInDefaults = computed(() => hasValidId(props.defaultValues?.experiment_id));
const hasSessionIdInDefaults = computed(() => hasValidId(props.defaultValues?.train_session_id));

const projectIdFromDefaults = computed(() => props.defaultValues?.project_id);
const projectNameFromDefaults = computed(() => props.defaultValues?.project_name || '');
const experimentIdFromDefaults = computed(() => props.defaultValues?.experiment_id);
const experimentNameFromDefaults = computed(() => props.defaultValues?.experiment_name || '');
const sessionIdFromDefaults = computed(() => props.defaultValues?.train_session_id);
const sessionNameFromDefaults = computed(() => props.defaultValues?.session_name || '');
const enableArtifacts = computed(() => !!field.value?.enable_artifacts);
const artifactFromDefaults = computed(() => props.defaultValues?.artifact_name || props.defaultValues?.artifact || '');
const artifactsFilter = computed(() => {
  const f = field.value?.artifacts_filter;
  return Array.isArray(f) && f.length > 0 ? f.map((ext) => String(ext).toLowerCase()) : null;
});

// Initialize from props
watch(() => props.projectID, (newValue) => {
  if (newValue) {
    selectedProjectId.value = parseInt(newValue);
    getExperiments(newValue);
  }
}, { immediate: true });

watch(() => props.experimentID, (newValue) => {
  if (newValue) {
    selectedExperimentId.value = parseInt(newValue);
    getTrainingSessions(newValue);
  }
}, { immediate: true });

async function getProjects() {
  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('project.list', {});
    
    if (response.error) {
      throw response.error;
    }
    return response.result || [];
  } catch (error) {
    console.error('Error fetching projects:', error);
    return [];
  }
}

async function getExperiments(projectId) {
  if (!projectId) {
    experiments.value = [];
    selectedExperimentId.value = null;
    return;
  }

  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.api('experiment.list', {
      project_id: parseInt(projectId),
    });
    
    if (response.error) {
      throw response.error;
    }
    experiments.value = response.result || [];
  } catch (error) {
    console.error('Error fetching experiments:', error);
    experiments.value = [];
  }
}

async function getTrainingSessions(experimentId) {
  if (!experimentId) {
    trainingSessions.value = [];
    selectedTrainingSessionId.value = null;
    artifacts.value = [];
    selectedArtifact.value = '';
    return;
  }

  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.getTrainingSessionList({
      trainer_id: parseInt(experimentId),
      reverse: true
    });
    
    if (response.error) {
      throw response.error;
    }
    trainingSessions.value = filterSessionsByModel(response.result || [], field.value?.model_type);
  } catch (error) {
    console.error('Error fetching training sessions:', error);
    trainingSessions.value = [];
    artifacts.value = [];
    selectedArtifact.value = '';
  }
}

async function getArtifacts(sessionId) {
  if (!enableArtifacts.value || !sessionId) {
    artifacts.value = [];
    selectedArtifact.value = '';
    return;
  }

  try {
    const dataConnect = new DatastoreConnect();
    const response = await dataConnect.getTrainingSessionArtifacts({
      training_session_id: parseInt(sessionId, 10),
    });

    if (response.error) {
      throw response.error;
    }

    artifacts.value = response.result || [];

    // Apply extension filter if artifacts_filter is specified
    if (artifactsFilter.value) {
      artifacts.value = artifacts.value.filter((artifact) => {
        const name = String(artifact.name || artifact).toLowerCase();
        return artifactsFilter.value.some((ext) => name.endsWith(ext));
      });
    }

    const hasDefaultArtifact = artifacts.value.some((artifact) => (artifact.name || artifact) === artifactFromDefaults.value);
    if (artifactFromDefaults.value && hasDefaultArtifact) {
      selectedArtifact.value = artifactFromDefaults.value;
    } else if (artifacts.value.length > 0) {
      selectedArtifact.value = artifacts.value[0].name || artifacts.value[0];
    } else {
      selectedArtifact.value = '';
    }
  } catch (error) {
    console.error('Error fetching artifacts:', error);
    artifacts.value = [];
    selectedArtifact.value = '';
  }
}

function emitTrainerValue() {
  emit('update:modelValue', {
    project_id: selectedProjectId.value,
    experiment_id: selectedExperimentId.value,
    train_session_id: selectedTrainingSessionId.value,
    artifact: selectedArtifact.value || '',
  });
}

function onChangeProject(projectId) {
  selectedProjectId.value = projectId;
  
  // Reset dependent selects
  selectedExperimentId.value = null;
  selectedTrainingSessionId.value = null;
  trainingSessions.value = [];
  artifacts.value = [];
  selectedArtifact.value = '';
  
  // Fetch experiments for the selected project
  if (projectId) {
    getExperiments(projectId);
  } else {
    experiments.value = [];
  }
  
  // Emit update
  emitTrainerValue();
}

function onChangeExperiment(experimentId) {
  selectedExperimentId.value = experimentId;
  
  // Reset training sessions
  selectedTrainingSessionId.value = null;
  artifacts.value = [];
  selectedArtifact.value = '';
  
  // Fetch training sessions for the selected experiment
  if (experimentId) {
    getTrainingSessions(experimentId);
  } else {
    trainingSessions.value = [];
  }
  
  // Emit update
  emitTrainerValue();
}

async function onChangeTrainingSession(sessionId) {
  selectedTrainingSessionId.value = sessionId;

  await getArtifacts(sessionId);
  emitTrainerValue();
}

function onChangeArtifact(artifactName) {
  selectedArtifact.value = artifactName;
  emitTrainerValue();
}

onMounted(async () => {
  // If defaultValues provide IDs, emit them as modelValue immediately
  if (hasSessionIdInDefaults.value) {
    selectedProjectId.value = props.defaultValues.project_id ?? null;
    selectedExperimentId.value = props.defaultValues.experiment_id ?? null;
    selectedTrainingSessionId.value = props.defaultValues.train_session_id;
    selectedArtifact.value = artifactFromDefaults.value;

    emit('update:modelValue', {
      project_id: props.defaultValues.project_id ?? null,
      experiment_id: props.defaultValues.experiment_id ?? null,
      train_session_id: props.defaultValues.train_session_id,
      artifact: artifactFromDefaults.value,
    });
  } else if (hasExperimentIdInDefaults.value) {
    selectedProjectId.value = props.defaultValues.project_id ?? null;
    selectedExperimentId.value = props.defaultValues.experiment_id;

    emit('update:modelValue', {
      project_id: props.defaultValues.project_id ?? null,
      experiment_id: props.defaultValues.experiment_id,
      train_session_id: null,
      artifact: '',
    });
  } else if (hasProjectIdInDefaults.value) {
    selectedProjectId.value = props.defaultValues.project_id;

    emit('update:modelValue', {
      project_id: props.defaultValues.project_id,
      experiment_id: null,
      train_session_id: null,
      artifact: '',
    });
  }

  // Get all projects
  projects.value = await getProjects();
  
  // If projectID is provided in props, use it
  if (props.projectID) {
    selectedProjectId.value = parseInt(props.projectID);
    await getExperiments(props.projectID);
    
    // If experimentID is also provided, use it
    if (props.experimentID) {
      selectedExperimentId.value = parseInt(props.experimentID);
      await getTrainingSessions(props.experimentID);

      if (!selectedTrainingSessionId.value && trainingSessions.value.length > 0 && !hasSessionIdInDefaults.value) {
        selectedTrainingSessionId.value = trainingSessions.value[0].id;
      }
    }
  } else if (projects.value && projects.value.length > 0) {
    // Default to first project if no projectID provided
    selectedProjectId.value = projects.value[0].id;
    await getExperiments(projects.value[0].id);
  }

  if (selectedTrainingSessionId.value) {
    await getArtifacts(selectedTrainingSessionId.value);
  }

  emitTrainerValue();
});
</script>

<style scoped>
.label-spacing {
  display: block;
  margin-top: 6px;
  margin-bottom: 4px;
}
</style>
